<template>
  <page-template :is-loading="isLoading" :data-exists="selectedGlobalFilterData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('globalFilter.configPage.newPageHeadline')
          : $t('globalFilter.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft v-if="!readOnly">
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Global Filters List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedGlobalFilter && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          :items="dropdownData.map((item) => [item.id, item.name])"
          :disabled="isLoading"
          v-model:value="selectedGlobalFilter"
          @update:value="switchGlobalFilter($event as string)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('globalFilter.configPage.switchTooltip')"
          classes="switch-global-filter switch-page-entry"
          data-qa="switch-global-filter"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight v-if="!readOnly">
      <template v-if="selectedGlobalFilter && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateGlobalFilter()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadGlobalFilter()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteGlobalFilterModal()"
          class="delete-button"
          :disabled="isLoading || selectedGlobalFilterProtected"
          :tooltip="deleteTooltip"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isGlobalFilterValid"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('globalFilter.configPage.name')"
              :placeholder="$t('globalFilter.configPage.name')"
              :disabled="systemManaged || readOnly"
              v-model:inputValue="selectedGlobalFilterData.name"
            />
          </div>
          <div class="rbz-field rbz-field-toggle">
            <rbz-form-toggle
              v-model:buttonValue="selectedGlobalFilterData.active"
              :label="$t('globalFilter.configPage.active')"
              data-qa="active-toggle"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="description"
              classes="description"
              data-qa="description-textarea"
              :label="$t('globalFilter.configPage.description')"
              v-model:inputValue="selectedGlobalFilterData.description"
              rows="2"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field is-relative">
            <rbz-form-input
              name="source"
              :label="$t('globalFilter.configPage.source')"
              classes="source"
              :placeholder="$t('globalFilter.configPage.source')"
              v-model:inputValue="selectedGlobalFilterData.source"
              data-qa="source-input"
              :disabled="systemManaged || readOnly"
            />
            <a
              v-if="externalSource"
              class="update-now-button rbz-text-small"
              :class="{ disabled: planetStore.isUpdateInProcess }"
              data-qa="update-now-button"
              tabindex="0"
              @click="fetchList"
              @keypress.space.prevent
              @keyup.space="fetchList"
              @keydown.enter="fetchList"
            >
              {{ $t('globalFilter.configPage.sourceUpdateNow') }}
            </a>
            <p class="help" v-if="externalSource" v-b-tooltip="fullFormattedModifiedDate">
              {{ $t('globalFilter.configPage.sourceUpdatedAt', { date: formattedModifiedDate }) }}
            </p>
          </div>
          <div class="rbz-field">
            <tags-selection
              :value="selectedGlobalFilterData.tags"
              @update:value="selectedGlobalFilterData.tags = $event as string[]"
              :label="$t('globalFilter.configPage.tags')"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-dropdown
              v-if="actions.length"
              :items="actions.map((item) => [item.id, item.name])"
              v-model:value="selectedGlobalFilterData.action"
              :label="$t('globalFilter.configPage.action')"
              class="action-selection"
              data-qa="action-dropdown"
              :disabled="readOnly"
            />
          </div>
        </div>
      </div>
      <div class="rbz-field">
        <label class="rbz-label">
          {{ $t('globalFilter.configPage.rule') }}
        </label>
        <global-filter-entries-relation-list
          v-model:rule="rule"
          :editable="editable"
          :disabled="readOnly"
          @invalid="isEntriesRelationListInvalid = $event"
        >
        </global-filter-entries-relation-list>
      </div>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :disabled="readOnly"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="
          loadGlobalFilter(selectedGlobalFilter);
          loadDropdownData();
        "
      />
      <rbz-modal-dialog
        :show="showDeleteGlobalFilterModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteGlobalFilter"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteGlobalFilterModal = false"
        @closeModal="showDeleteGlobalFilterModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', { name: selectedGlobalFilterData.name })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import {
  ApiPlanet,
  ApiTrustedSource,
  BasicDocument,
  Action,
  DocumentName,
  GenericObject,
  GlobalFilter,
  GlobalFilterRule,
  GlobalFilterRuleEntry,
  GlobalFilterRuleSection,
  GlobalFilterRuleWithIds,
  GlobalFilterRuleEntryWithIds,
  GlobalFilterRuleSectionWithIds,
} from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import TagsSelection from '@/components/TagsSelection.vue';
import RbzFormToggle from '@/components/RbzFormToggle.vue';
import { AxiosError, AxiosResponse } from 'axios';
import DateTimeUtils from 'ui-component-library/helpers/DateTimeUtils';
import GlobalFilterEntriesRelationList from '@/components/GlobalFilterEntriesRelationList.vue';
import GitHistory from '@/components/GitHistory.vue';
import ipaddr from 'ipaddr.js';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { usePlanetStore } from '@/store/PlanetStore';
import { useTagsStore } from '@/store/TagsStore';
import { useUserStore } from '@/store/UserStore';
import { useRouting } from '@/helpers/RoutingUtils';

type TrustedSource = {
  id?: string;
  source: string;
  address?: string;
  globalFilter?: string;
  comment: string;
};

const ASN_REGEX = /(as\d{3,6})/i;

export default defineComponent({
  name: 'GlobalFiltersEditor',
  setup() {
    const { getEditPath, getNewPath, getListPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      getListPath,
      navigateTo,
    };
  },
  components: {
    RbzFormButton,
    RbzFormInput,
    RbzFormTextarea,
    RbzFormToggle,
    GitHistory,
    GlobalFilterEntriesRelationList,
    LayoutDividerLine,
    PageTemplate,
    RbzDropdown,
    RbzModalDialog,
    TagsSelection,
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dropdownData: [] as BasicDocument[],
      isNew: false,
      selectedGlobalFilter: null as string,
      selectedGlobalFilterData: null as GlobalFilter,
      originalGlobalFilterData: null as GlobalFilter,
      rule: null as GlobalFilterRuleWithIds,
      pageLoadingCounter: null as number | null,
      isDeleteLoading: false,
      showDeleteGlobalFilterModal: false,

      isEntriesRelationListInvalid: false,
      actions: null as Action[],
      formattedModifiedDate: null as string | null,
      fullFormattedModifiedDate: null as string | null,
      trustedSourcesData: [] as TrustedSource[],
    };
  },
  watch: {
    $route: {
      handler: async function (val) {
        if (['New Global Filter', 'Global Filters Editor'].includes(val.name)) {
          this.setPageLoadingStatus(true);
          this.isNew = val.name.startsWith('New');
          await this.branchesStore.getBranches();
          const dropdownDataPromise = this.loadDropdownData();
          const actionsPromise = this.loadActions();
          await Promise.all([dropdownDataPromise, actionsPromise]);
          if (val.name === 'New Global Filter') {
            this.selectedGlobalFilterData = this.newGlobalFilter();
            this.selectedGlobalFilterData.name = Utils.generateUniqueEntityName(
              this.selectedGlobalFilterData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalGlobalFilterData = _.cloneDeep(this.selectedGlobalFilterData);
            this.rule = this.addRuleIdsDeep(
              this.normalizeGFRule(this.selectedGlobalFilterData.rule)
            );
          }
          if (val.name === 'Global Filters Editor') {
            const globalFilterId = this.$route.params?.doc_id?.toString();
            const dataExists = this.dropdownData.some((item) => {
              return item.id === globalFilterId;
            });
            if (dataExists) {
              await this.loadGlobalFilter(globalFilterId);
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedGlobalFilterData = JSON.parse(savedSessionData);
            this.rule = this.addRuleIdsDeep(
              this.normalizeGFRule(this.selectedGlobalFilterData.rule)
            );
          }
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      deep: true,
      immediate: true,
    },
    rule: {
      handler: function () {
        if (this.selectedGlobalFilterData) {
          this.selectedGlobalFilterData.rule = this.removeRuleIdsDeep(
            this.rule
          ) as GlobalFilterRuleSection;
        }
      },
      deep: true,
    },
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, usePlanetStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('globalFilter.plural'),
        singular: this.$t('globalFilter.singular'),
      };
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/global-filters/versions`;
    },

    isLoading(): boolean {
      return (
        this.pageLoadingCounter === null || this.pageLoadingCounter > 0 || this.isDeleteLoading
      );
    },

    entityNotSaved(): boolean {
      return !_.isEqual(this.selectedGlobalFilterData, this.originalGlobalFilterData);
    },

    selectedGlobalFilterProtected(): boolean {
      return this.systemManaged || this.usedByTrustedSource;
    },

    deleteTooltip(): string {
      if (this.selectedGlobalFilterProtected) {
        if (this.systemManaged) {
          return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
        }
        if (this.usedByTrustedSource) {
          return this.$t('globalFilter.configPage.deleteDisabledTooltip.referencedTrustedSource');
        }
      }
      return '';
    },

    systemManaged(): boolean {
      return this.selectedGlobalFilter?.startsWith('waap-');
    },

    usedByTrustedSource(): boolean {
      return this.trustedSourcesData.some(
        (trusted_net) =>
          trusted_net.source === 'global_filter' &&
          trusted_net.globalFilter === this.selectedGlobalFilter
      );
    },

    editable(): boolean {
      return this.selfManaged && !this.systemManaged;
    },

    selfManaged(): boolean {
      return this.selectedGlobalFilterData?.source === 'self-managed';
    },

    externalSource(): boolean {
      return this.selectedGlobalFilterData?.source?.startsWith('http');
    },

    isGlobalFilterValid(): boolean {
      const entriesListValid = !this.isEntriesRelationListInvalid;
      const trimmedName = this.selectedGlobalFilterData.name.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      const nameValid =
        this.selectedGlobalFilterData.name &&
        (trimmedName === this.originalGlobalFilterData.name.toLowerCase().trim() || !nameExists);
      return entriesListValid && nameValid;
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('global-filters'));
    },

    switchGlobalFilter(globalFilterId: string) {
      this.navigateTo(this.getEditPath('global-filters', globalFilterId));
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/global-filters`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    newGlobalFilter(): GlobalFilter {
      const factory = DatasetsUtils.newEntityFactory['global-filters'];
      return factory && factory();
    },

    async loadGlobalFilter(globalFilterId: string) {
      this.setPageLoadingStatus(true);
      this.selectedGlobalFilter = globalFilterId;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/global-filters/${globalFilterId}`,
      });
      this.selectedGlobalFilterData = response.data || {};
      this.originalGlobalFilterData = _.cloneDeep(this.selectedGlobalFilterData);
      this.rule = this.addRuleIdsDeep(this.normalizeGFRule(this.selectedGlobalFilterData.rule));
      this.setPageLoadingStatus(false);
    },

    normalizeGFRule(rule: GlobalFilterRule): GlobalFilterRuleSection {
      if (!rule) {
        return { relation: 'OR', entries: [] };
      }
      if (Array.isArray(rule)) {
        return { relation: 'OR', entries: [rule] } as GlobalFilterRuleSection;
      }
      return rule as GlobalFilterRuleSection;
    },

    addRuleIdsDeep(rule: GlobalFilterRule): GlobalFilterRuleWithIds {
      let value: GlobalFilterRuleSectionWithIds['value'] | GlobalFilterRuleEntryWithIds['value'];
      const isRecursive = rule.entries?.length > 0;
      if (isRecursive) {
        value = {
          relation: (rule as GlobalFilterRuleSection).relation,
          entries: _.map((rule as GlobalFilterRuleSection).entries, (entry) => {
            return this.addRuleIdsDeep(entry);
          }),
        };
      } else {
        value = rule as GlobalFilterRuleEntry;
      }
      return {
        id: DatasetsUtils.generateUUID2(),
        value,
      } as GlobalFilterRuleWithIds;
    },

    removeRuleIdsDeep(rule: GlobalFilterRuleWithIds): GlobalFilterRule {
      let value: GlobalFilterRule;
      const isRecursive = rule.value.entries?.length > 0;
      if (isRecursive) {
        value = {
          relation: (rule as GlobalFilterRuleSectionWithIds).value.relation,
          entries: _.map((rule as GlobalFilterRuleSectionWithIds).value.entries, (entry) => {
            return this.removeRuleIdsDeep(entry);
          }),
        };
      } else {
        value = (rule as GlobalFilterRuleEntryWithIds).value;
      }
      return value;
    },

    async duplicateGlobalFilter() {
      this.setPageLoadingStatus(true);
      const globalFilterToAdd = _.cloneDeep(this.selectedGlobalFilterData);
      globalFilterToAdd.id = DatasetsUtils.generateUUID2();
      globalFilterToAdd.name = Utils.generateUniqueEntityName(
        globalFilterToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      const url = `conf/${this.selectedBranch}/global-filters/${globalFilterToAdd.id}`;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: globalFilterToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.switchGlobalFilter(globalFilterToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    downloadGlobalFilter() {
      Utils.downloadFile('global-filter', 'json', this.selectedGlobalFilterData);
    },

    openDeleteGlobalFilterModal() {
      this.showDeleteGlobalFilterModal = true;
    },

    async deleteGlobalFilter() {
      this.isDeleteLoading = true;
      const globalFilterId = this.selectedGlobalFilter;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === globalFilterId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedGlobalFilter = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/global-filters/${globalFilterId}`,
        successMessage,
        failureMessage,
      });
      this.redirectToList();
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      this.selectedGlobalFilterData.name = this.selectedGlobalFilterData.name.trim();
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/global-filters/${this.selectedGlobalFilterData.id}`,
        data: this.selectedGlobalFilterData,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedGlobalFilterData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          await this.loadDropdownData();
          this.originalGlobalFilterData = _.cloneDeep(this.selectedGlobalFilterData);
        }
      }
      this.setPageLoadingStatus(false);
    },

    async loadActions() {
      this.setPageLoadingStatus(true);
      const displayedDefaultIds = [
        'action-global-filter-block',
        'action-waap-feed-block',
        'action-https-redirect',
        'action-challenge',
        'action-monitor',
        'action-skip',
      ];
      const displayedTypes: Action['type'][] = [
        'skip',
        'block',
        'challenge',
        'ichallenge',
        'monitor',
      ];
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/actions`,
        config: { headers: { 'x-fields': 'id, name, type' } },
      });
      this.actions = _.filter(response?.data?.items, (item: Action) => {
        const hiddenDefaultId =
          item.id.startsWith('action-') && !displayedDefaultIds.includes(item.id);
        return displayedTypes.includes(item.type) && !hiddenDefaultId;
      });
      this.setPageLoadingStatus(false);
    },

    async loadTrustedSources() {
      const url = `conf/${this.selectedBranch}/planets`;
      const methodName = 'GET';
      const response = await RequestsUtils.sendRequest({ methodName, url });
      const data: ApiPlanet = response?.data || {};
      this.trustedSourcesData = _.map(data.trusted_nets, (trusted: ApiTrustedSource) => {
        return {
          id: DatasetsUtils.generateUUID2(),
          source: trusted.source,
          address: trusted.address,
          globalFilter: trusted.gf_id,
          comment: trusted.comment,
        };
      });
    },

    lineParser(data: string, entryCallback: (value: string) => GlobalFilterRuleEntry) {
      if (!_.isString(data)) {
        return [];
      }
      const entries: GlobalFilterRuleEntry[] = [];
      const splitData = data?.split('\n');
      _.forEach(splitData, (dataEntry) => {
        const entryToAdd = entryCallback(dataEntry);
        if (entryToAdd) {
          entries.push(entryToAdd);
        }
      });
      return entries;
    },

    objectParser(data: GenericObject, store: GlobalFilterRuleEntry[]) {
      _.each(data, (item) => {
        let entryToAdd: GlobalFilterRuleEntry;
        if (_.isArray(item) && (item.length === 2 || item.length === 3)) {
          const annotation = _.isString(item[2]) ? item[2].trim() : null;
          // IP
          if (_.isString(item[0]) && _.isString(item[1]) && item[0].toLowerCase() === 'ip') {
            entryToAdd = this.ipParser(item[1], annotation);
          }
          // ASN
          if (_.isString(item[0]) && _.isString(item[1]) && item[0].toLowerCase() === 'asn') {
            if (ASN_REGEX.test(item[1])) {
              entryToAdd = ['asn', item[1], annotation];
            }
          }
          // Add entry to rule if found
          if (entryToAdd) {
            store.push(entryToAdd);
          } else {
            this.objectParser(item, store);
          }
        } else if (_.isObject(item)) {
          this.objectParser(item, store);
        } else if (_.isString(item)) {
          entryToAdd = this.ipParser(item, null);
          if (entryToAdd) {
            store.push(entryToAdd);
          } else if (ASN_REGEX.test(item)) {
            entryToAdd = ['asn', item, null];
            store.push(entryToAdd);
          }
        }
      });
    },

    ipParser(ip: string, annotation: string = null): GlobalFilterRuleEntry {
      const splitValue = ip.split('/');
      const isIP = Utils.isIP(splitValue[0]);
      if (!isIP) {
        return;
      }
      let isCidr = true;
      if (splitValue[1]) {
        const ipv4 = Utils.isIPv4(splitValue[0]);
        try {
          if (ipv4) {
            isCidr = ipaddr.IPv4.networkAddressFromCIDR(ip).octets.join('.') === splitValue[0];
          } else {
            const normalizedIP = ipaddr.parse(splitValue[0]).toNormalizedString();
            isCidr =
              _.map(ipaddr.IPv6.networkAddressFromCIDR(ip).parts, (part) => {
                return part.toString(16);
              }).join(':') === normalizedIP;
          }
        } catch {
          isCidr = false;
        }
      }
      if (isCidr) {
        return ['ip', ip, annotation];
      }
    },

    fetchList() {
      const url = this.selectedGlobalFilterData.source;
      RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `tools/fetch?url=${url}`,
        onFail: (error: AxiosError) => {
          const data = error.response?.data;
          if (data) {
            Utils.toast({
              message: data as string,
              type: 'is-danger',
            });
          }
        },
      }).then((response: AxiosResponse) => {
        let data = response?.data || [];
        const convertedData = data as GlobalFilter;
        if (convertedData?.rule?.entries?.length) {
          this.rule = this.addRuleIdsDeep(convertedData.rule);
          this.selectedGlobalFilterData.mdate = new Date().toISOString();
          return;
        }
        let entries: GlobalFilterRuleEntry[];
        entries = this.lineParser(data, (entry: string) => {
          const splitEntry = entry?.split('#');
          return this.ipParser(splitEntry?.[0], splitEntry?.[1]);
        });
        if (entries.length === 0) {
          entries = this.lineParser(data, (entry: string) => {
            const splitEntry = entry?.split('#');
            if (ASN_REGEX.test(splitEntry?.[0])) {
              return ['asn', splitEntry?.[0], splitEntry?.[1] || null] as GlobalFilterRuleEntry;
            }
          });
        }
        if (entries.length === 0) {
          if (_.isString(data)) {
            try {
              data = JSON.parse(data);
            } catch {
              // Non-JSON string: leave data as-is and let objectParser handle it
            }
          }
          this.objectParser(data, entries);
        }
        if (entries.length > 0) {
          this.rule = this.addRuleIdsDeep({
            entries: entries,
            relation: 'OR',
          });
          this.selectedGlobalFilterData.mdate = new Date().toISOString();
          Utils.toast({
            message: this.$t('globalFilter.configPage.sourceUpdateSuccess'),
            type: 'is-success',
          });
        } else {
          Utils.toast({
            message: this.$t('globalFilter.configPage.sourceUpdateFailure'),
            type: 'is-danger',
          });
        }
      });
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedGlobalFilterData, this.entityNotSaved],
        (params) => {
          const [formData, entityNotSaved] = params;
          Utils.saveSessionData(formData as GlobalFilter, entityNotSaved as boolean);
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },
  },
  created() {
    const tagsStore = useTagsStore();
    this.$watch(
      () => [tagsStore.editorDocId, tagsStore.editorDocType],
      async ([docId, docType]) => {
        if (!docId || docType !== 'global-filters') {
          return;
        }
        await this.branchesStore.getBranches();
        await this.loadActions();
        await this.loadGlobalFilter(docId);
      },
      { immediate: true }
    );
    setInterval(() => {
      this.formattedModifiedDate = this.selectedGlobalFilterData?.mdate
        ? DateTimeUtils.isoToNowCuriefenseFormat(this.selectedGlobalFilterData.mdate)
        : 'N/A';
      this.fullFormattedModifiedDate = this.selectedGlobalFilterData?.mdate
        ? DateTimeUtils.isoToNowFullCuriefenseFormat(this.selectedGlobalFilterData.mdate)
        : 'N/A';
    }, 1000);
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
  mounted() {
    this.loadTrustedSources();
  },
});
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.update-now-button {
  color: map.get(colors.$colors, 'bright-gray');
  position: absolute;
  right: 0;
  top: 0;

  &.disabled {
    color: map.get(colors.$colors, 'silver');
    pointer-events: none;
  }
}
</style>
